import React from 'react';
import { connect } from 'react-redux';
import { BaseDialog } from '../layout/Dialog';
import './SegmentationDialog.css';
import { clearRosterUpload, uploadRoster } from '../../actions/roster';
import { attachRoster } from '../../actions/event';
import Event from '../../models/Event';
import Logger from '../../util/Logger';
import { handleError, handleErrorWithDialog } from '../../util/Errors';
import { NavigationUtils } from '../../util/NavigationUtils';
import { PulseLoader } from 'react-spinners';
import { API_POPIN_PREFIX } from '../../constants';
import { showDialog } from '../../actions/dialog';
import Roster from '../../models/Roster';
import sampleTemplate from '../../assets/sample_segmentation_template.csv';
import store from '../../store/configureStore';
import { CreatorReportAnalytics } from '../../analytics/CreatorReportAnalytics';

const NewSegmentationFlowSteps = {
  NOT_STARTED: 0,
  BEGIN_NEW_LIST: 1,
  UPLOAD_LIST: 2,
  LIST_UPLOADED: 3,
  LIST_UPLOAD_ERROR: 4
};

const PastOccurrenceSegmentationFlowSteps = {
  NOT_STARTED: 0,
  CONFIRM: 1
};

class SegmentationDialog extends React.Component {

  constructor (props) {
    super(props);

    this.browseButtonRef = null;
    this.state = {
      newSegmentationFlowStep: NewSegmentationFlowSteps.NOT_STARTED,
      pastOccurrenceSegmentationFlowStep: PastOccurrenceSegmentationFlowSteps.NOT_STARTED,
      editedListFile: null
    };
  }

  advanceNewSegmentationFlow = ($event, step) => {
    if ($event) {
      $event.preventDefault();
    }

    this.trackModalStep(step);
    this.setNewSegmentationFlowStep(step);
  };

  setNewSegmentationFlowStep = (step) => {
    this.setState({ newSegmentationFlowStep: step });
  };

  advancePastOccurrenceSegmentationFlow = ($event, step) => {
    if ($event) {
      $event.preventDefault();
    }

    this.trackModalStep(step);
    this.setPastOccurrenceSegmentationFlowStep(step);
  };

  setPastOccurrenceSegmentationFlowStep = (step) => {
    this.setState({ pastOccurrenceSegmentationFlowStep: step });
  };

  trackCancelClicked = (step) => {
    if (step === NewSegmentationFlowSteps.UPLOAD_LIST) {
      CreatorReportAnalytics.didShowSegUploadModal('cancel');
    } else if (step === NewSegmentationFlowSteps.NOT_STARTED) {
      CreatorReportAnalytics.didShowSegWhatTodoModal('cancel');
    } else if (step === NewSegmentationFlowSteps.BEGIN_NEW_LIST) {
      CreatorReportAnalytics.segInfoModal('cancel');
    } else if (step === NewSegmentationFlowSteps.LIST_UPLOADED) {
      // do nothing there is no other way out
    }
  };

  onCancelClicked = ($event) => {
    $event.preventDefault();
    const { newSegmentationFlowStep } = this.state;
    this.trackCancelClicked(newSegmentationFlowStep);

    // Call through to the parent to unmount the component
    store.dispatch(clearRosterUpload());
    this.onDialogHidden();
  };

  onDialogHidden = () => {
    const { onHidden } = this.props;
    onHidden();
  };

  renderNewSegmentationFlow = () => {
    const renderUploadBegin = () => {
      return (
        <div className="col">
          <div className="row no-gutters text-title">
            Segment your results with POPin
          </div>
          <div className="row no-gutters mt-3">
            <div className="col">
              POPin lets you filter your results so you can see if certain groups
              behave differently than other groups; this is called segmenting.
              Segmenting results is a great way to get a granular view of your
              results and gain insight into what your subgroups are thinking.
              <div className="my-3" />
              POPin protects the integrity of anonymity within your session by
              blurring data results if there aren't enough responses within a
              subgroup for segmentation. We protect the trust between you
              and your viewers by locking segmentation once activity occurs
              within your POPin.
              &nbsp;<span className="link" onClick={ this.onHelpClicked }>Learn More</span>
            </div>
          </div>
          <div className="row no-gutters mt-3">
            <button className="btn pop-btn-secondary" onClick={ this.onCancelClicked }>
              Cancel
            </button>

            <div className="fix-ie" />

            <button
              className="btn pop-btn" onClick={ $event => {
              this.advanceNewSegmentationFlow($event, NewSegmentationFlowSteps.UPLOAD_LIST);
            } }>
              Begin Segmentation
            </button>
          </div>
        </div>
      );
    };
    const renderUploadList = () => {
      const { loading, rosterAttaching } = this.props;
      const { editedListFile } = this.state;

      const onUploadClicked = ($event) => {
        $event.preventDefault();

        const { editedListFile } = this.state;
        if (!!editedListFile) {
          CreatorReportAnalytics.didShowSegUploadModal('upload');
          const { uploadRoster } = this.props;
          uploadRoster(editedListFile);
        }
      };

      const handlDownloadTemplate = ($event) => {
        $event.preventDefault();
        CreatorReportAnalytics.downloadSegTemplate();
        NavigationUtils.openTab(`${API_POPIN_PREFIX}/rosters/template`);
      };

      const openSampleTemplate = ($event) => {
        $event.preventDefault();
        CreatorReportAnalytics.downloadSampleTemplate();
        NavigationUtils.openTab(sampleTemplate);
      };

      return (
        <div className="col">
          <div className="row no-gutters h-100">
            <span className="text-title">Upload a list to segment your results</span>
            <i className="material-icons my-auto mx-3 clickable" onClick={ this.onHelpClicked }>
              help_outline
            </i>
          </div>

          <div className="row no-gutters mt-3">
            <div className="col">
              <div className="font-weight-bold">
                STEP 1: Download our template
              </div>
              <div className="link" onClick={ handlDownloadTemplate }>
                <i className="material-icons my-auto mr-3">get_app</i>
                <span>Download Template</span>
              </div>
            </div>
          </div>

          <div className="row no-gutters mt-3">
            <div className="col">
              <div className="font-weight-bold">
                STEP 2: Edit the template with your segmentation information
              </div>
              <div>
                Your file must contain headers for each column
                <br />
                (10 columns max) with column A titled as "email"
              </div>
              <div className="link" onClick={ openSampleTemplate }>
                Download Sample Template
              </div>
            </div>
          </div>

          <div className="row no-gutters mt-3">
            <div className="col">
              <div className="font-weight-bold">
                STEP 3: Upload your edited list
              </div>

              <div className="row no-gutters">
                <form className="col-8" onSubmit={ this.handleBrowseClicked }>
                  <input
                    type="text"
                    onChange={ $event => $event.preventDefault() }
                    className="pop-input pop-form-control w-100 mw-100"
                    value={ !!editedListFile ? editedListFile.name : '' }
                  />
                </form>
                <div className="col-4 my-auto clearfix">
                  <button
                    disabled={ loading }
                    className="btn pop-btn float-right"
                    onClick={ this.handleBrowseClicked }>
                    Browse
                  </button>
                </div>
              </div>

            </div>
          </div>

          <div className="row no-gutters mt-3">
            <div className="fix-ie" />

            <button
              disabled={ loading }
              className="btn pop-btn-secondary mr-0 mr-sm-3" onClick={ this.onCancelClicked }>
              Cancel
            </button>

            <button
              disabled={ !editedListFile || !editedListFile.name || loading || rosterAttaching }
              className="btn pop-btn" onClick={ onUploadClicked }>
              { (loading || rosterAttaching)
                ? <PulseLoader color="#FFFFFF" size={ 10 } />
                : 'Upload'
              }
            </button>
          </div>

        </div>
      );
    };
    const renderUploadSuccess = () => {
      const { roster, loading } = this.props;

      const rosterSize = Roster.getClientLocals(roster).getSize();
      const emailString = `${rosterSize} ${rosterSize === 1 ? 'email' : 'emails'}`;
      return (
        <div className="col">
          <div className="row no-gutters h-100 text-title">
            List uploaded successfully
          </div>

          <div className="row no-gutters mt-3">
            <div className="col">
              Your uploaded segmentation contains { emailString }.
              You can edit your segmentation list up until this POPin has activity.
              <div className="my-3" />
              Once activity has begun, your segmentation is locked from further
              changes to ensure the anonymity of users.
              &nbsp;<span className="link" onClick={ this.onHelpClicked }>Learn More</span>
            </div>
          </div>

          <div className="row no-gutters mt-3">
            <div className="fix-ie" />

            <button
              disabled={ loading }
              className="btn pop-btn mr-0 mr-sm-3" onClick={ this.onCancelClicked }>
              Ok
            </button>
          </div>

        </div>
      );
    };
    const renderUploadError = () => {
      const { loading, error } = this.props;
      return (
        <div className="col">
          <div className="row no-gutters h-100 text-title">
            List upload error
          </div>

          { /* Display the actual error message */ }
          { (!!error && !!error.errorMessage) && (
            <div className="row no-gutters mt-3">
              <div>Error: { error.errorMessage }</div>
            </div>
          ) }

          <div className="row no-gutters mt-3">
            <div className="col creator-reports-editing p-3">
              <div className="row no-gutters">
                <span className="font-weight-bold font-size-md">
                  Please check your file and try again
                </span>
                <i className="material-icons my-auto mx-3 clickable" onClick={ this.onHelpClicked }>
                  help_outline
                </i>
              </div>
              <div className="font-size-sm text-error">
                - Must be a CSV (.csv).<br />
                - Restricted to 10 columns maximum.<br />
                - First column header (column A, row 1) must be named 'email'.<br />
                - First row of each column must contain a header name.<br />
                - Cannot contain dates.<br />
              </div>
            </div>
          </div>

          <div className="row no-gutters mt-3">
            <div className="fix-ie" />

            <button
              disabled={ loading }
              className="btn pop-btn-secondary mr-0 mr-sm-3" onClick={ this.onCancelClicked }>
              Cancel
            </button>

            <button
              disabled={ loading }
              className="btn pop-btn" onClick={ $event => {
              this.advanceNewSegmentationFlow($event, NewSegmentationFlowSteps.UPLOAD_LIST);
            } }>
              Retry
            </button>
          </div>

        </div>
      );
    };

    const { newSegmentationFlowStep } = this.state;
    if (newSegmentationFlowStep === NewSegmentationFlowSteps.BEGIN_NEW_LIST) {
      return renderUploadBegin();
    } else if (newSegmentationFlowStep === NewSegmentationFlowSteps.UPLOAD_LIST) {
      return renderUploadList();
    } else if (newSegmentationFlowStep === NewSegmentationFlowSteps.LIST_UPLOADED) {
      return renderUploadSuccess();
    } else if (newSegmentationFlowStep === NewSegmentationFlowSteps.LIST_UPLOAD_ERROR) {
      return renderUploadError();
    } else {
      Logger.assert('New Segmentation Flow rendered with invalid step', newSegmentationFlowStep);
    }
  };

  getPastOccurrenceRosterId = () => {
    const { event } = this.props;
    if (event) {
      return Event.getSuggestedRosterId(event);
    } else {
      return null;
    }
  };

  downloadRoster = ($event) => {
    $event.preventDefault();
    CreatorReportAnalytics.downloadExisting();

    // Some reason, this event gets outdated when we upload rosters a couple of times...
    // pull straight from Redux to always have the latest
    const event = store.getState().event.event;

    const rosterId = Event.getRosterId(event);
    if (!!rosterId) {
      const url = `${API_POPIN_PREFIX}/rosters/${rosterId}/export`;
      NavigationUtils.openTab(url);
    }
  };

  trackHelpClicked = (step) => {
    if (step === NewSegmentationFlowSteps.NOT_STARTED) {
      CreatorReportAnalytics.segWhatTodoHelp();
    } else if (step === NewSegmentationFlowSteps.BEGIN_NEW_LIST) {
      CreatorReportAnalytics.segInfoLearnMore();
    } else if (step === NewSegmentationFlowSteps.UPLOAD_LIST) {
      CreatorReportAnalytics.infoIconClicked();
    } else if (step === NewSegmentationFlowSteps.LIST_UPLOADED) {
      CreatorReportAnalytics.uploadSuccessLearnMoreClicked();
    }
  };

  renderInitialDialogState = () => {
    const { event } = this.props;
    return (
      <div className="row no-gutters h-100">
        <div className="col text-center align-self-center">
          <div className="text-title mb-3">What would you like to do:</div>

          { !Event.getNotEditableIncludes(event, 'roster_id') && (
            <div
              className="link"
              onClick={ $event => {
                this.advanceNewSegmentationFlow($event, NewSegmentationFlowSteps.BEGIN_NEW_LIST);
              } }>
              Upload a new list
            </div>
          ) }

          { (!!this.getPastOccurrenceRosterId() &&
            !Event.getNotEditableIncludes(event, 'roster_id')) &&
          (
            <div
              className="link"
              onClick={ $event => {
                this.advancePastOccurrenceSegmentationFlow($event,
                  PastOccurrenceSegmentationFlowSteps.CONFIRM);
              } }>
              Use list from last occurrence
            </div>
          ) }

          { !!Event.getRosterId(event) && (
            <div className="link" onClick={ this.downloadRoster }>
              Download existing list
            </div>
          ) }

          <div className="mt-3">
            <button
              className="btn pop-btn-secondary"
              onClick={ this.onCancelClicked }>Cancel
            </button>
          </div>

        </div>
      </div>
    );
  };

  renderPastOccurrenceSegmentationFlow = () => {

    const onOkClicked = ($event) => {
      $event.preventDefault();

      const { event, attachRoster } = this.props;
      const rosterId = this.getPastOccurrenceRosterId();
      if (!!rosterId) {
        attachRoster(Event.getId(event), rosterId);
      }
    };

    const { loading, rosterAttaching } = this.props;
    return (
      <div className="col">
        <div className="row no-gutters h-100 text-title">
          You are about to apply the list from the last occurrence to this POPin.
        </div>
        <div className="row no-gutters">
          Do you want to use the list of emails from your last occurrence of the series to segment
          the results of this POPin?
        </div>

        <div className="row no-gutters mt-3">
          <div className="fix-ie" />

          <button
            disabled={ loading }
            className="btn pop-btn-secondary mr-0 mr-sm-3" onClick={ this.onCancelClicked }>
            Cancel
          </button>

          <button
            disabled={ loading || rosterAttaching }
            className="btn pop-btn" onClick={ onOkClicked }>
            Ok
          </button>
        </div>

      </div>
    );
  };

  setBrowseButtonRef = ($element) => {
    this.browseButtonRef = $element;
  };

  handleBrowseClicked = ($event) => {
    $event.preventDefault();
    CreatorReportAnalytics.browseClicked();
    if (this.browseButtonRef) {
      this.browseButtonRef.click();
    }
  };

  onEditedListFileChanged = ($event) => {
    const files = $event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.setState({
        editedListFile: file
      });
    }
  };

  trackModalStepLastOccurrence = (step) => {
    if (step === PastOccurrenceSegmentationFlowSteps.CONFIRM) {
      CreatorReportAnalytics.useListFromPastOccurrence();
    }
  };

  trackModalStep = (step) => {
    if (step === NewSegmentationFlowSteps.BEGIN_NEW_LIST) {
      CreatorReportAnalytics.didShowSegWhatTodoModal('upload new list');
    } else if (step === NewSegmentationFlowSteps.UPLOAD_LIST) {
      CreatorReportAnalytics.segInfoModal('begin');
    } else if (step === NewSegmentationFlowSteps.LIST_UPLOADED) {
      CreatorReportAnalytics.uploadSuccess();
    } else if (step === NewSegmentationFlowSteps.LIST_UPLOAD_ERROR) {
      CreatorReportAnalytics.uploadError();
    }
  };

  componentDidUpdate (prevProps) {
    const { event, roster, error, attachError } = this.props;
    const { pastOccurrenceSegmentationFlowStep, newSegmentationFlowStep } = this.state;
    handleError(error, prevProps.error);
    handleErrorWithDialog(attachError, prevProps.attachError, 'Failed to apply Segmentation');

    if (newSegmentationFlowStep === NewSegmentationFlowSteps.UPLOAD_LIST) {
      // If we are done uploading the roster
      if (!!error && !prevProps.error) {
        // If an error occurs, we show the error state page
        this.advanceNewSegmentationFlow(null, NewSegmentationFlowSteps.LIST_UPLOAD_ERROR);
      }

      // Did not encounter any errors, and have a valid roster
      if (!error && !!roster && !prevProps.roster) {
        // We associate the roster to the event
        const { attachRoster } = this.props;
        // Upload the roster to the event, on success close dialog.
        Logger.info('Attaching roster to event!');
        attachRoster(Event.getId(event), Roster.getClientLocals(roster).getId());
      }

      if (!!event && !!roster && !!Event.getRosterId(event)) {
        // We have uploaded and associated a roster
        Logger.info('Roster associated to event!');
        this.advanceNewSegmentationFlow(null, NewSegmentationFlowSteps.LIST_UPLOADED);
        const { onActivated } = this.props;
        onActivated(true);
      }
    }

    if (pastOccurrenceSegmentationFlowStep === PastOccurrenceSegmentationFlowSteps.CONFIRM) {
      this.trackModalStepLastOccurrence(PastOccurrenceSegmentationFlowSteps.CONFIRM);
      if (!!event && !!Event.getRosterId(event)) {
        // We have uploaded and associated a roster
        const { onActivated } = this.props;
        onActivated(true);
      }
    }
  }

  render () {
    const { newSegmentationFlowStep, pastOccurrenceSegmentationFlowStep } = this.state;
    let body;
    if (newSegmentationFlowStep === NewSegmentationFlowSteps.NOT_STARTED &&
      pastOccurrenceSegmentationFlowStep === PastOccurrenceSegmentationFlowSteps.NOT_STARTED) {
      body = this.renderInitialDialogState();
    } else if (newSegmentationFlowStep > NewSegmentationFlowSteps.NOT_STARTED) {
      body = this.renderNewSegmentationFlow();
    } else if (pastOccurrenceSegmentationFlowStep >
      PastOccurrenceSegmentationFlowSteps.NOT_STARTED) {
      body = this.renderPastOccurrenceSegmentationFlow();
    } else {
      Logger.assert('Invalid segmentation dialog state:', newSegmentationFlowStep,
        pastOccurrenceSegmentationFlowStep);
    }

    return (
      <div>
        { /* The input has to be here or the file picker dialog will not show up. */ }
        <input
          type="file"
          id="editedListFileUpload"
          ref={ this.setBrowseButtonRef }
          accept="text/csv"
          className="d-none"
          onChange={ this.onEditedListFileChanged } />

        <BaseDialog
          show={ true }
          closeOnClickOutside={ false }
          customModalDialogClass="segmentation-dialog"
          customModalBodyClass="segmentation-body"
          body={ body }
          hideDialog={ this.onDialogHidden }
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  const user = state.user.user;
  return {
    user: user,
    loading: state.roster.loading,
    error: state.roster.error,
    rosterAttaching: state.event.rosterLoading,
    attachError: state.event.rosterError
  };
};

export default connect(mapStateToProps, {
  showDialog,
  attachRoster,
  uploadRoster,
  clearRosterUpload
})(SegmentationDialog);
